const normalize = (value = "") =>
  String(value || "")
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]/g, "");

const normalizeIdentity = (value = "") =>
  String(value || "")
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, " ")
    .trim();

const includesIdentity = (value, identity) => {
  const normalizedValue = normalizeIdentity(value);
  const normalizedIdentity = normalizeIdentity(identity);
  return (
    !normalizedIdentity ||
    ` ${normalizedValue} `.includes(` ${normalizedIdentity} `)
  );
};

const readComponent = (components = [], type, field = "long_name") =>
  components.find((component) => component?.types?.includes(type))?.[field] ||
  "";

const matchesWhenResolved = (entered, ...resolvedValues) => {
  if (!String(entered || "").trim()) return true;
  const normalizedEntered = normalize(entered);
  const normalizedResolved = resolvedValues.map(normalize).filter(Boolean);
  return (
    normalizedResolved.length === 0 ||
    normalizedResolved.includes(normalizedEntered)
  );
};

export const evaluateStreetAddressGeocodeResult = (
  result = {},
  address = {},
  { source = "geocode" } = {},
) => {
  if (!String(address?.line1 || "").trim()) {
    return { valid: true, reason: "non_street_query" };
  }
  if (result?.partial_match === true) {
    return { valid: false, reason: "partial_match" };
  }

  const components = Array.isArray(result?.address_components)
    ? result.address_components
    : [];
  const resultTypes = Array.isArray(result?.types) ? result.types : [];
  const hasType = (type) => resultTypes.includes(type);
  const hasComponent = (type) =>
    components.some((component) => component?.types?.includes(type));
  const hasStreetIdentity =
    hasComponent("street_number") && hasComponent("route");
  const hasPremiseIdentity =
    hasComponent("premise") || hasComponent("subpremise");
  const isOrdinaryStreet = hasType("street_address");
  const isPremise = hasType("premise") || hasType("subpremise");

  if (
    (!isOrdinaryStreet || !hasStreetIdentity) &&
    (!isPremise || (!hasStreetIdentity && !hasPremiseIdentity))
  ) {
    return { valid: false, reason: "not_a_specific_street_address" };
  }

  const locationType = String(
    result?.geometry?.location_type || "",
  ).toUpperCase();
  if (
    locationType &&
    !["ROOFTOP", "RANGE_INTERPOLATED"].includes(locationType)
  ) {
    return { valid: false, reason: "imprecise_location" };
  }
  if (source !== "places" && !locationType) {
    return { valid: false, reason: "missing_location_precision" };
  }

  const resolvedCity =
    readComponent(components, "locality") ||
    readComponent(components, "postal_town") ||
    readComponent(components, "sublocality_level_1") ||
    readComponent(components, "administrative_area_level_3");
  const resolvedStateLong = readComponent(
    components,
    "administrative_area_level_1",
  );
  const resolvedStateShort = readComponent(
    components,
    "administrative_area_level_1",
    "short_name",
  );
  const resolvedZip = readComponent(components, "postal_code");
  const resolvedStreetNumber = readComponent(components, "street_number");
  const resolvedPremise =
    readComponent(components, "premise") ||
    readComponent(components, "subpremise");
  const resolvedCountry = readComponent(components, "country", "short_name");

  if (!matchesWhenResolved(address?.city, resolvedCity)) {
    return { valid: false, reason: "city_mismatch" };
  }
  if (
    !matchesWhenResolved(
      address?.stateCode || address?.state,
      resolvedStateLong,
      resolvedStateShort,
    )
  ) {
    return { valid: false, reason: "state_mismatch" };
  }
  if (
    address?.zipCode &&
    resolvedZip &&
    String(address.zipCode).trim().slice(0, 5) !==
      String(resolvedZip).trim().slice(0, 5)
  ) {
    return { valid: false, reason: "zip_mismatch" };
  }
  if (resolvedCountry && resolvedCountry.toUpperCase() !== "US") {
    return { valid: false, reason: "unsupported_country" };
  }

  const includesResolvedIdentity = isPremise
    ? includesIdentity(address?.line1, resolvedPremise)
    : !resolvedStreetNumber ||
      includesIdentity(address?.line1, resolvedStreetNumber);
  const enteredLine1 = normalize(address?.line1);
  const includesDuplicatedLocality = Boolean(
    normalize(resolvedCity) &&
    normalize(resolvedZip) &&
    enteredLine1.includes(normalize(resolvedCity)) &&
    enteredLine1.includes(normalize(resolvedZip)),
  );

  return {
    valid: true,
    reason: isPremise ? "specific_premise" : "specific_street_address",
    ...(source !== "places" &&
    (!includesResolvedIdentity || includesDuplicatedLocality)
      ? { requiresReview: true }
      : {}),
  };
};

export const isStreetAddressGeocodeResult = (...args) =>
  evaluateStreetAddressGeocodeResult(...args).valid;
